import { useEffect, useRef } from 'react';

import { mixComponentsToRgb, rgbToCss } from '@/utils/gameMixColor.utils';

import './ArtGamesAmbient.css';

const BASE_COLORS = {
  red: { rgb: { r: 255, g: 0, b: 0 } },
  yellow: { rgb: { r: 255, g: 255, b: 0 } },
  blue: { rgb: { r: 0, g: 0, b: 255 } },
  white: { rgb: { r: 255, g: 255, b: 255 } },
  black: { rgb: { r: 0, g: 0, b: 0 } },
};
const COLOR_KEYS = Object.keys(BASE_COLORS);
const MAX_BALLS = 10;
const PRIMARY_COLOR_KEYS = new Set(['red', 'yellow', 'blue']);

const EMPTY_COLOR = { r: 236, g: 236, b: 240 };

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function createEmptyMix() {
  return COLOR_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
}

function colorFromComponents(components) {
  return rgbToCss(mixComponentsToRgb(components, BASE_COLORS, EMPTY_COLOR));
}

function combineComponents(a, b) {
  const combined = createEmptyMix();

  for (let i = 0; i < COLOR_KEYS.length; i += 1) {
    const key = COLOR_KEYS[i];
    combined[key] = (a[key] ?? 0) + (b[key] ?? 0);
  }

  return combined;
}

export default function ArtGamesAmbient() {
  const ambientRef = useRef(null);
  const blobRefs = useRef([]);
  const bloomRefs = useRef([]);

  useEffect(() => {
    const ambientEl = ambientRef.current;
    if (!ambientEl) {
      return undefined;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const blobs = blobRefs.current.filter(Boolean);
    const blooms = bloomRefs.current.filter(Boolean);

    const particles = [];
    const coolDown = new Map();
    let globalSpawnLockUntil = 0;
    let bloomIndex = 0;
    let width = 0;
    let height = 0;
    let rafId = 0;
    let resizeObserver = null;

    const readBounds = () => {
      const bounds = ambientEl.getBoundingClientRect();
      width = Math.max(280, bounds.width);
      height = Math.max(220, bounds.height);
    };

    const renderBlobs = () => {
      for (let i = 0; i < blobs.length; i += 1) {
        const blob = blobs[i];
        const particle = particles[i];

        if (!blob || !particle) {
          if (blob) {
            blob.style.opacity = '0';
          }
          continue;
        }

        blob.style.opacity = '1';
        blob.style.width = `${particle.radius * 2}px`;
        blob.style.height = `${particle.radius * 2}px`;
        blob.style.background = particle.color;
        blob.style.boxShadow = `0 0 ${18 + particle.radius}px ${particle.color}88`;
        blob.style.transform = `translate3d(${particle.x - particle.radius}px, ${particle.y - particle.radius}px, 0)`;
      }
    };

    const initParticles = () => {
      readBounds();
      for (let i = 0; i < COLOR_KEYS.length; i += 1) {
        const colorKey = COLOR_KEYS[i];
        const radius = PRIMARY_COLOR_KEYS.has(colorKey)
          ? 24 + Math.random() * 14
          : 16 + Math.random() * 10;
        const components = createEmptyMix();
        components[colorKey] = 1;
        particles.push({
          x: radius + Math.random() * Math.max(1, width - radius * 2),
          y: radius + Math.random() * Math.max(1, height - radius * 2),
          vx: (Math.random() * 2 - 1) * 0.65,
          vy: (Math.random() * 2 - 1) * 0.65,
          radius,
          components,
          color: colorFromComponents(components),
        });
      }
      renderBlobs();
    };

    const spawnBloom = (x, y, radius, color) => {
      if (!blooms.length) {
        return;
      }

      const bloom = blooms[bloomIndex % blooms.length];
      bloomIndex += 1;
      bloom.style.transition = 'none';
      bloom.style.left = `${x}px`;
      bloom.style.top = `${y}px`;
      bloom.style.width = `${radius * 1.1}px`;
      bloom.style.height = `${radius * 1.1}px`;
      bloom.style.background = color;
      bloom.style.opacity = '0';
      bloom.style.transform = 'translate3d(-50%, -50%, 0) scale(0.45)';

      requestAnimationFrame(() => {
        bloom.style.transition = 'transform 700ms ease-out, opacity 760ms ease-out';
        bloom.style.transform = 'translate3d(-50%, -50%, 0) scale(1.7)';
        bloom.style.opacity = '0';
      });
    };

    const resolveCollision = (a, b, dx, dy, dist) => {
      const nx = dx / dist;
      const ny = dy / dist;
      const p = a.vx * nx + a.vy * ny - b.vx * nx - b.vy * ny;
      a.vx -= p * nx;
      a.vy -= p * ny;
      b.vx += p * nx;
      b.vy += p * ny;

      const overlap = (a.radius + b.radius - dist) / 2;
      a.x -= overlap * nx;
      a.y -= overlap * ny;
      b.x += overlap * nx;
      b.y += overlap * ny;
    };

    let lastTimestamp = performance.now();
    const tick = now => {
      const dt = Math.min(2, (now - lastTimestamp) / 16.6667);
      lastTimestamp = now;
      let spawnedThisFrame = false;

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        if (p.x <= p.radius || p.x >= width - p.radius) {
          p.vx *= -1;
          p.x = clamp(p.x, p.radius, width - p.radius);
        }
        if (p.y <= p.radius || p.y >= height - p.radius) {
          p.vy *= -1;
          p.y = clamp(p.y, p.radius, height - p.radius);
        }
      }

      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const a = particles[i];
          const b = particles[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const minDist = a.radius + b.radius;
          const distSq = dx * dx + dy * dy;

          if (distSq > minDist * minDist || distSq <= 0.0001) {
            continue;
          }

          const dist = Math.sqrt(distSq);
          resolveCollision(a, b, dx, dy, dist);

          const pairKey = `${i}:${j}`;
          const last = coolDown.get(pairKey) || 0;
          if (now - last <= 180) {
            continue;
          }

          coolDown.set(pairKey, now);
          const mixedComponents = combineComponents(a.components, b.components);
          const mixed = colorFromComponents(mixedComponents);
          spawnBloom((a.x + b.x) / 2, (a.y + b.y) / 2, minDist * 0.9, mixed);

          if (!spawnedThisFrame && now >= globalSpawnLockUntil && particles.length < MAX_BALLS) {
            const newRadius = clamp((a.radius + b.radius) * 0.5, 14, 26);
            particles.push({
              x: clamp((a.x + b.x) * 0.5, newRadius, width - newRadius),
              y: clamp((a.y + b.y) * 0.5, newRadius, height - newRadius),
              vx: (Math.random() * 2 - 1) * 0.8,
              vy: (Math.random() * 2 - 1) * 0.8,
              radius: newRadius,
              components: mixedComponents,
              color: mixed,
            });
            spawnedThisFrame = true;
            globalSpawnLockUntil = now + 180;
          }
        }
      }

      renderBlobs();
      rafId = requestAnimationFrame(tick);
    };

    initParticles();

    if (!reducedMotion) {
      rafId = requestAnimationFrame(tick);
    }

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        readBounds();
        for (let i = 0; i < particles.length; i += 1) {
          const p = particles[i];
          p.x = clamp(p.x, p.radius, width - p.radius);
          p.y = clamp(p.y, p.radius, height - p.radius);
        }
      });
      resizeObserver.observe(ambientEl);
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return (
    <div className="art-games-page__ambient" aria-hidden="true" ref={ambientRef}>
      {Array.from({ length: 10 }).map((_, index) => (
        <span
          key={`blob-${index}`}
          className="art-games-page__blob"
          ref={node => {
            blobRefs.current[index] = node;
          }}
        />
      ))}
      {Array.from({ length: 8 }).map((_, index) => (
        <span
          key={`bloom-${index}`}
          className="art-games-page__bloom"
          ref={node => {
            bloomRefs.current[index] = node;
          }}
        />
      ))}
    </div>
  );
}
